/**
 * BallStrip
 *
 * Shows all Balls in a Catchball Loop with their status badges.
 * Embedded in CatchballDashboard loop rows. (Also used by LoopWorkspacePage until that
 * surface was retired 2026-08-28.)
 *
 * "One inquiry, many branches. Each branch received a Ball,
 *  deliberated locally, and returned a Proposal."
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  Circle,
  Clock,
  MessageSquare,
  Plus,
  RotateCcw,
} from 'lucide-react';
import { useCatchballBallStore, useBallsForLoop } from '../../../stores/prioritization/catchballBallStore';
import {
  BALL_STATUS_LABELS,
  ballDirection,
  ballHasReturnedProposal,
  type CatchballBall,
  type BallStatus,
} from '../../../types/prioritization/catchballBall';
import { OpenThreadsChip } from './handoff/OpenThreadsChip';
import { CatchballUndoAction } from './CatchballUndoAction';
import { recallBall } from '../../../lib/reversals/reversalsService';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { isAnchorBall } from '../../../types/prioritization/catchballBall';

// ── Status icon ───────────────────────────────────────────────────────────────

function BallStatusIcon({ status }: { status: BallStatus }) {
  const dir = ballDirection(status);
  if (status === 'accepted' || status === 'reconciled') {
    return <CheckCircle2 className="h-4 w-4 text-[var(--green)] shrink-0" />;
  }
  if (status === 'accepted_provisional') {
    return <CheckCircle2 className="h-4 w-4 text-[var(--text-ter)] shrink-0" />;
  }
  if (status === 'proposal_returned' || status === 'under_review') {
    return <ArrowUp className="h-4 w-4 text-[var(--text-ter)] shrink-0" />;
  }
  if (status === 'returned_for_revision') {
    return <RotateCcw className="h-4 w-4 text-[var(--mustard)] shrink-0" />;
  }
  if (status === 'draft') {
    return <Circle className="h-4 w-4 text-[var(--text-ter)] shrink-0" />;
  }
  if (dir === 'downward') {
    return <ArrowDown className="h-4 w-4 text-[var(--text-ter)] shrink-0" />;
  }
  return <Clock className="h-4 w-4 text-[var(--text-ter)] shrink-0" />;
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: BallStatus }) {
  const base = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium';
  let cls = '';
  switch (status) {
    case 'accepted':
    case 'reconciled':
      cls = 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'; break;
    case 'accepted_provisional':
      cls = 'bg-[var(--slate-dim)] text-[var(--text-sec)]'; break;
    case 'proposal_returned':
    case 'under_review':
      cls = 'bg-[var(--slate-dim)] text-[var(--text-sec)]'; break;
    case 'returned_for_revision':
      cls = 'bg-[var(--mustard-dim)] text-[var(--mustard)]'; break;
    case 'draft':
    case 'closed':
      cls = 'bg-[var(--hover-tint)] text-[var(--text-ter)]'; break;
    default:
      cls = 'bg-[var(--slate-dim)] text-[var(--text-sec)]';
  }
  return <span className={`${base} ${cls}`}>{BALL_STATUS_LABELS[status]}</span>;
}

// ── Single ball row ───────────────────────────────────────────────────────────

function BallRow({
  ball,
  cycleId,
  linkToBall,
  childCount = 0,
  onRecalled,
}: {
  ball: CatchballBall;
  cycleId?: string;
  linkToBall: boolean;
  childCount?: number;
  onRecalled?: () => void;
}) {
  const hasProposal = ballHasReturnedProposal(ball);
  const orgLabel = ball.recipientOrgUnitName ?? ball.recipientUserName ?? null;
  const indentPx = Math.min(ball.depth, 4) * 16;
  const userId = useUserStore((s) => s.userId);

  // "I am the sender, state untouched" (canon 27 §6 rule 2 / migration 738
  // recall_ball preconditions) — the server remains the authority on the rest
  // (nobody has claimed the loop's ball, no children, no proposal, no passed
  // turn, no session). A refusal for any of those still surfaces in the modal.
  const canRecall =
    !!userId &&
    (ball.sentByUserId === userId || ball.createdBy === userId) &&
    ball.ballStatus === 'sent' &&
    !ball.openedAt &&
    !hasProposal &&
    childCount === 0;

  const inner = (
    <div
      className="flex items-center justify-between gap-3 px-4 py-3 group hover:bg-[var(--surface)] transition-colors"
      style={{ paddingLeft: 16 + indentPx }}
    >
      <div className="flex items-center gap-3 min-w-0">
        <BallStatusIcon status={ball.ballStatus} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-[var(--text-pri)] truncate">
            {ball.name}
            {ball.depth > 0 && (
              <span className="ml-1.5 text-xs font-normal text-[var(--text-ter)]">depth {ball.depth}</span>
            )}
          </p>
          {orgLabel && (
            <p className="text-xs text-[var(--text-ter)] truncate">
              {orgLabel}
              {childCount > 0 && (
                <span className="text-[var(--text-ter)]"> · {childCount} child{childCount === 1 ? '' : 'ren'}</span>
              )}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {hasProposal && (
          <span className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)]">
            <MessageSquare className="h-3 w-3" />
            Proposal returned
          </span>
        )}
        <StatusBadge status={ball.ballStatus} />
        {canRecall && (
          <CatchballUndoAction
            triggerLabel="Recall"
            modalTitle="Recall this ball"
            description={`Nobody at ${orgLabel ?? 'the recipient'} has opened or claimed it yet — recalling puts it back in your drafts.`}
            confirmLabel="Recall"
            reasonPlaceholder="Why recall this ball? (required)"
            onConfirm={(reason) => recallBall(ball.id, reason)}
            onSuccess={onRecalled}
            successTitle="Ball recalled"
            successMessage="It is back in your drafts."
          />
        )}
      </div>
    </div>
  );

  if (linkToBall && cycleId) {
    return (
      <Link
        to={`/prioritization/cascade/ball/${cycleId}/${ball.loopId}/${ball.id}`}
        className="block"
      >
        {inner}
      </Link>
    );
  }
  return <div>{inner}</div>;
}

// ── Main component ────────────────────────────────────────────────────────────

interface BallStripProps {
  loopId: string;
  cycleId?: string;
  /** If true, each ball row links to the Ball Detail page */
  linkToBalls?: boolean;
  /** If provided, shows an "Add Ball" button */
  onAddBall?: () => void;
  /** Whether to show a compact version (no heading, tighter) */
  compact?: boolean;
}

export function BallStrip({
  loopId,
  cycleId,
  linkToBalls = true,
  onAddBall,
  compact = false,
}: BallStripProps) {
  const fetchBallsForLoop = useCatchballBallStore((s) => s.fetchBallsForLoop);
  const isLoading = useCatchballBallStore((s) => s.isLoading);
  const loopBalls = useBallsForLoop(loopId);
  const balls = useMemo(() => loopBalls.filter((ball) => !isAnchorBall(ball)), [loopBalls]);

  useEffect(() => {
    fetchBallsForLoop(loopId);
  }, [loopId, fetchBallsForLoop]);

  // Depth-first order so parent → children read as a rollout tree.
  const orderedBalls = (() => {
    const byParent = new Map<string | null, CatchballBall[]>();
    for (const ball of balls) {
      const key = ball.parentBallId;
      const list = byParent.get(key) ?? [];
      list.push(ball);
      byParent.set(key, list);
    }
    const out: CatchballBall[] = [];
    const walk = (parentId: string | null) => {
      const kids = (byParent.get(parentId) ?? []).sort(
        (a, b) => a.sequenceInLoop - b.sequenceInLoop,
      );
      for (const kid of kids) {
        out.push(kid);
        walk(kid.id);
      }
    };
    walk(null);
    // Orphans (parent missing from this loop fetch) append at end.
    for (const ball of balls) {
      if (!out.includes(ball)) out.push(ball);
    }
    return out;
  })();

  const childCountByParent = balls.reduce<Record<string, number>>((acc, b) => {
    if (b.parentBallId) acc[b.parentBallId] = (acc[b.parentBallId] ?? 0) + 1;
    return acc;
  }, {});

  const returnedCount = balls.filter(ballHasReturnedProposal).length;
  const totalCount = balls.length;
  const hasTree = balls.some((b) => b.parentBallId != null);

  if (isLoading && balls.length === 0) {
    return (
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-4 text-xs text-[var(--text-ter)]">
        Loading Balls…
      </div>
    );
  }

  if (balls.length === 0) {
    return (
      <div className={` border border-dashed border-[var(--rule)] bg-[var(--bg-raised)] ${compact ? 'p-3' : 'p-4'}`}>
        <div className="flex items-center justify-between">
          <p className="text-xs text-[var(--text-ter)]">No Balls in this loop yet.</p>
          {onAddBall && (
            <button
              type="button"
              onClick={onAddBall}
              className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
            >
              <Plus className="h-3 w-3" />
              Add Ball
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      {!compact && (
        <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]">
          <div>
            <div className="flex items-center gap-2">
              <h4 className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">
                {hasTree ? 'Rollout — balls in this loop' : 'Balls in this loop'}
              </h4>
              <span className="normal-case tracking-normal">
                <OpenThreadsChip loopId={loopId} />
              </span>
            </div>
            {totalCount > 0 && (
              <p className="text-xs text-[var(--text-ter)] mt-0.5">
                {returnedCount} of {totalCount} {totalCount === 1 ? 'ball has' : 'balls have'} returned a Proposal
                {hasTree && ' · parallel branches active'}
              </p>
            )}
          </div>
          {onAddBall && (
            <button
              type="button"
              onClick={onAddBall}
              className="inline-flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-2.5 py-1 text-xs font-medium text-[var(--text-sec)] hover:bg-[var(--surface)] hover:text-[var(--text-pri)]"
            >
              <Plus className="h-3 w-3" />
              Add Ball
            </button>
          )}
        </div>
      )}
      <div className="divide-y divide-[var(--rule)]">
        {orderedBalls.map((ball) => (
          <BallRow
            key={ball.id}
            ball={ball}
            cycleId={cycleId}
            linkToBall={linkToBalls}
            childCount={childCountByParent[ball.id] ?? 0}
            onRecalled={() => fetchBallsForLoop(loopId)}
          />
        ))}
      </div>
    </div>
  );
}
